import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getToken } from '../../api';

const MEN_OPTIONS = [
  { label: 'Tall and slender',   sub: 'lean, angular energy',       doshas: ['V','P'] },
  { label: 'Thick and stable',   sub: 'solid, grounded presence',   doshas: ['K','P'] },
  { label: 'Muscular and thin',  sub: 'defined, fiery build',       doshas: ['P','V'] },
  { label: 'Muscular and thick', sub: 'powerful, dense frame',      doshas: ['P','K'] },
  { label: 'Soft and cozy',      sub: 'warm, rounded softness',     doshas: ['K','V'] },
  { label: 'Thin and gentle',    sub: 'slight, delicate presence',  doshas: ['V','K'] },
];

const WOMEN_OPTIONS = [
  { label: 'Thick and gentle',   sub: 'full, soft curves',          doshas: ['K','V'] },
  { label: 'Strong and elegant', sub: 'toned, regal frame',         doshas: ['P','K'] },
  { label: 'Thin and tall',      sub: 'lean, graceful build',       doshas: ['V','P'] },
  { label: 'Thick and muscular', sub: 'powerful, lush presence',    doshas: ['K','P'] },
  { label: 'Strong and delicate',sub: 'defined yet fine features',  doshas: ['P','V'] },
  { label: 'Delicate and soft',  sub: 'airy, gentle softness',      doshas: ['V','K'] },
];

function scoreSelections(options, selected) {
  const counts = { V: 0, P: 0, K: 0 };
  selected.forEach(i => options[i].doshas.forEach(d => counts[d]++));
  const total = Math.max(Object.values(counts).reduce((a, b) => a + b, 0), 1);
  const vata  = Math.round((counts.V / total) * 100);
  const pitta = Math.round((counts.P / total) * 100);
  const kapha = Math.round((counts.K / total) * 100);
  const dominant = counts.V >= counts.P && counts.V >= counts.K ? 'vata'
                 : counts.P >= counts.K ? 'pitta' : 'kapha';
  return { vata, pitta, kapha, dominant };
}

function QuizSection({ title, options, selected, onToggle }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{ fontSize: 10, letterSpacing: 2.5, textTransform: 'uppercase', color: 'var(--very-muted)', marginBottom: 16 }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {options.map((opt, i) => {
          const isSelected = selected.includes(i);
          return (
            <button
              key={i}
              onClick={() => onToggle(i)}
              style={{
                background: isSelected ? 'rgba(201,168,76,0.1)' : '#0f0f0f',
                border: isSelected ? '1px solid rgba(201,168,76,0.5)' : '1px solid #1e1e1e',
                borderRadius: 8,
                padding: '14px 16px',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: 15, color: isSelected ? '#c9a84c' : '#fff', fontWeight: 400, marginBottom: 3 }}>
                {opt.label}
              </div>
              <div style={{ fontSize: 12, color: isSelected ? 'rgba(201,168,76,0.6)' : 'var(--very-muted)' }}>
                {opt.sub}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function AttractionQuiz() {
  const navigate = useNavigate();
  const [orientation, setOrientation] = useState(null);
  const [gender, setGender] = useState(null);
  const [selectedMen, setSelectedMen] = useState([]);
  const [selectedWomen, setSelectedWomen] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getToken()) { navigate('/login'); return; }
    api.get('/profile').then(p => {
      setOrientation((p.orientation || '').toLowerCase());
      setGender((p.gender || '').toLowerCase());
    }).catch(() => {});
  }, [navigate]);

  const showMen = orientation === 'men' ||
    (orientation === 'everyone') ||
    (orientation === 'straight' && ['woman','women','female'].includes(gender));

  const showWomen = orientation === 'women' ||
    orientation === 'everyone' ||
    (!showMen && orientation !== 'men') ||
    (orientation === 'straight' && !['woman','women','female'].includes(gender));

  const toggleMen = (i) => {
    setSelectedMen(prev =>
      prev.includes(i) ? prev.filter(x => x !== i) : prev.length < 3 ? [...prev, i] : prev
    );
  };

  const toggleWomen = (i) => {
    setSelectedWomen(prev =>
      prev.includes(i) ? prev.filter(x => x !== i) : prev.length < 3 ? [...prev, i] : prev
    );
  };

  const canSubmit = (!showMen || selectedMen.length > 0) && (!showWomen || selectedWomen.length > 0);

  const handleSubmit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);

    let vata = 0, pitta = 0, kapha = 0, dominant = 'vata';

    if (showMen && showWomen) {
      const m = scoreSelections(MEN_OPTIONS, selectedMen);
      const w = scoreSelections(WOMEN_OPTIONS, selectedWomen);
      vata  = Math.round((m.vata  + w.vata)  / 2);
      pitta = Math.round((m.pitta + w.pitta) / 2);
      kapha = Math.round((m.kapha + w.kapha) / 2);
      dominant = vata >= pitta && vata >= kapha ? 'vata' : pitta >= kapha ? 'pitta' : 'kapha';
    } else if (showMen) {
      ({ vata, pitta, kapha, dominant } = scoreSelections(MEN_OPTIONS, selectedMen));
    } else {
      ({ vata, pitta, kapha, dominant } = scoreSelections(WOMEN_OPTIONS, selectedWomen));
    }

    try {
      await api.post('/profile', {
        quiz_attraction_vata:     vata,
        quiz_attraction_pitta:    pitta,
        quiz_attraction_kapha:    kapha,
        quiz_attraction_dominant: dominant,
        quiz_test_completed:      true,
      });
      navigate('/onboarding/attraction-photos', { replace: true });
    } catch {
      setSaving(false);
    }
  };

  const handleSkip = () => navigate('/onboarding/attraction-photos', { replace: true });

  if (orientation === null) return <div style={{ height: '100vh', background: '#000' }} />;

  return (
    <div style={{ minHeight: '100vh', background: '#0d0d0d', color: '#fff', padding: '56px 24px 80px' }}>

      <div style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: '#c9a84c', marginBottom: 24 }}>
        Attraction Profile
      </div>

      <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 300, marginBottom: 10, lineHeight: 1.3 }}>
        What draws you in?
      </div>
      <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.65, marginBottom: 40 }}>
        Pick up to 3 types that genuinely attract you. This shapes how we weight your matches.
      </div>

      {showMen && (
        <QuizSection
          title={showWomen ? 'In men I like' : 'Body types I like'}
          options={MEN_OPTIONS}
          selected={selectedMen}
          onToggle={toggleMen}
        />
      )}

      {showWomen && (
        <QuizSection
          title={showMen ? 'In women I like' : 'Body types I like'}
          options={WOMEN_OPTIONS}
          selected={selectedWomen}
          onToggle={toggleWomen}
        />
      )}

      <button
        onClick={handleSubmit}
        disabled={!canSubmit || saving}
        style={{
          width: '100%', padding: '16px 0', borderRadius: 8, border: 'none',
          background: canSubmit && !saving ? '#c9a84c' : '#1e1e1e',
          color: canSubmit && !saving ? '#000' : '#444',
          fontSize: 14, letterSpacing: 1,
          cursor: canSubmit && !saving ? 'pointer' : 'default',
          marginBottom: 16, transition: 'all 0.2s',
        }}
      >
        {saving ? 'Saving...' : 'Set My Attraction Type'}
      </button>

      <button
        onClick={handleSkip}
        style={{
          width: '100%', padding: '12px 0', background: 'none', border: 'none',
          color: 'var(--very-muted)', fontSize: 12, cursor: 'pointer',
        }}
      >
        Skip for now
      </button>
    </div>
  );
}
